import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox, FormControlLabel, FormGroup, Grid } from '@mui/material';
import { DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { booleanSelectList, ListId, LocalStorange } from '../../../shared/environment';
import { AccessRoleDTO, AccessRoleFilterDTO, AttributeDTO, FeatureDTO, FeatureFilterDTO, TestAttributeValueDTO, TestComponentDTO, UserDTO, ValueListDTO, ValueListFilterDTO } from '../../../shared/interfaces';
import { formatDateTime } from '../../../shared/utils';

interface IAttributeMaintenanceProps {
    data: any;
    parentId?: string;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: true) => void;
    isAttributeValue: boolean | false;
}

export const AttributeMaintenance: React.FC<IAttributeMaintenanceProps> = ({ data, parentId, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted, isAttributeValue }) => {
    const [actionList, setActionList] = useState<ValueListDTO[]>([]);
    const [identificationTypeIList, setIdentificationTypeList] = useState<ValueListDTO[]>([]);
    const [featureList, setFeatureList] = useState<FeatureDTO[]>([]);
    const [attributeDTO, setAttributeDTO] = useState<AttributeDTO>();
    const [testComponentDTO, setTestComponentDTO] = useState<TestComponentDTO>();
    const [testAttributeValueDTO, setTestAttributeValueDTO] = useState<TestAttributeValueDTO>();

    const [saveAttributeValue, setSaveAttributeValue] = useState(false);
    const [userList, setUserList] = useState<UserDTO[]>([]);

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        !saveAttributeValue && setAttributeDTO({
            ...attributeDTO,
            id: '',
            name: '',
            featureId: !isAttributeValue ? parentId ?? '' : testComponentDTO?.featureId ?? '',
            actionId: '',
            automationId: '',
            identificationTypeId: '',
            orderExecution: 0,
            defaultValue: '',
            description: ''
        } as AttributeDTO);

        !saveAttributeValue && setTestAttributeValueDTO({
            ...testAttributeValueDTO,
            id: '',
            value: '',
            attributeId: data.attrbiuteId ?? '',
            testComponentId: parentId ?? ''
        } as TestAttributeValueDTO);

        if(isOpenMaintenance){
            handleFeatureGetAllAsync();
            handleActionGetAllAsync();
            handleIdentificationTypeIdGetAllAsync();
            handleUserGetAll();

            if (isAttributeValue) {
                handleTestComponentGetByIdAsync(parentId ?? data.testComponentId);
                handleAttributeGetByIdAsync(data.attributeId);
                handleTestAttributeValueGetByIdAsync(data.id);
            }
            else {
                handleAttributeGetByIdAsync(data.id);
            }

            handleGetPermission(ListId.appsphere.attributeId, setAccessRolePermissionDTO);
        }
    }, [data, isOpenMaintenance]);

    useEffect(() => {
        if (attributeDTO?.id !== '') {
            if (attributeDTO?.id !== undefined) {
                saveAttributeValue && handleSaveTestAttributeValue(attributeDTO?.id);
                setSaveAttributeValue(false);
            }
        }
    }, [saveAttributeValue, attributeDTO?.id]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList,);
    }, []);
    
    const handleUserGetAll = useCallback(async () => {
        const filterUser = {
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('authsphere.user', filterUser, setLoading, setUserList, showMessage);
    }, []);

    const handleActionGetAllAsync = useCallback(async () => {
        const valueListFilterDTO: ValueListFilterDTO = {
            listId: ListId.appsphere.attribute.actionId,
            orderBy: 'value'
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setActionList, showMessage);
    }, []);

    const handleIdentificationTypeIdGetAllAsync = useCallback(async () => {
        const valueListFilterDTO: ValueListFilterDTO = {
            listId: ListId.appsphere.attribute.identificationtypeId,
            orderBy: 'value'
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setIdentificationTypeList, showMessage);
    }, []);

    const handleFeatureGetAllAsync = useCallback(async () => {
        const featureFilterDTO: FeatureFilterDTO = {
            name: '',
            applicationId: '',
        };
        GenericService.GetAllAsync1('appsphere.feature', featureFilterDTO, setLoading, setFeatureList, showMessage);
    }, []);

    const handleAttributeGetByIdAsync = useCallback(async (id: string) => {
        GenericService.GetByIdAsync1('appsphere.attribute', id, setLoading, setAttributeDTO, showMessage);
    }, [data]);

    const handleTestAttributeValueGetByIdAsync = useCallback(async (id: string) => {
        GenericService.GetByIdAsync1('testingsphere.testAttributeValue', id, setLoading, setTestAttributeValueDTO, showMessage);
    }, [data]);

    const handleTestComponentGetByIdAsync = useCallback(async (id: string) => {
        GenericService.GetByIdAsync1('testingsphere.testComponent', id, setLoading, setTestComponentDTO, showMessage);
    }, [data]);

    const handleSaveAttributeClick = useCallback(async () => {
        const dto = attributeDTO as AttributeDTO;
        if (attributeDTO?.id === '' || attributeDTO?.id === undefined) {
            // dto.defaultValue = attributeDTO?.defaultValue ?? '';
            dto.featureId = !isAttributeValue ? parentId ?? '' : testComponentDTO?.featureId ?? '';
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('appsphere.attribute', dto, setAttributeDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            if(!accessRolePermissionDTO?.allowUpdate) {
                showMessage('There is no permission for this access.', 'info');
                return;
            }
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('appsphere.attribute', dto, setActionExecuted, setLoading, showMessage);
        }
        if (isAttributeValue) {
            setSaveAttributeValue(true);
        }
    }, [attributeDTO]);

    const handleSaveTestAttributeValue = useCallback(async (id: string) => {
        const dto = testAttributeValueDTO as TestAttributeValueDTO;
        if (testAttributeValueDTO?.id === '' || testAttributeValueDTO?.id === undefined) {
            dto.attributeId = id;
            dto.testComponentId = parentId ?? '';
            dto.orderExecution = testAttributeValueDTO?.orderExecution ?? 0;
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('testingsphere.testattributevalue', dto, setTestAttributeValueDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('testingsphere.testattributevalue', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [testAttributeValueDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: 'Save', icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveAttributeClick },
        { id: 'cancel', text: 'Cancel', icon: 'cancel_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title={isAttributeValue ? 'TEST ATTRIBUTE VALUE DETAILS' : 'ATTRIBUTE DETAILS'}
                icon='abc_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={9}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                value={attributeDTO?.name}
                                setValue={setAttributeDTO}
                                obj={attributeDTO}
                                required={true}
                                maxLength={200}
                                autoFocus={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='featureId'
                                label='Feature Name'
                                value={!isAttributeValue ? attributeDTO?.featureId : testComponentDTO?.featureId}
                                setValue={setAttributeDTO}
                                obj={attributeDTO}
                                valueList={featureList}
                                required={true}
                                disabled={parentId ?? attributeDTO?.id ? true : false}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='actionId'
                                label='Action'
                                value={attributeDTO?.actionId}
                                setValue={setAttributeDTO}
                                obj={attributeDTO}
                                valueList={actionList}
                                required={true}
                            />
                        </Grid>
                        {ListId.ListValue.ActionsTest.navigateId !== attributeDTO?.actionId ? (
                            <React.Fragment>
                                <Grid item xs={12} md={6}>
                                    <TextFieldInput
                                        id='automationId'
                                        label='Automation Identification'
                                        value={attributeDTO?.automationId}
                                        setValue={setAttributeDTO}
                                        obj={attributeDTO}
                                        maxLength={200}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <SelectField
                                        id='identificationTypeId'
                                        label='Identification Type'
                                        value={attributeDTO?.identificationTypeId}
                                        setValue={setAttributeDTO}
                                        obj={attributeDTO}
                                        valueList={identificationTypeIList}
                                        hasNoneSelect={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={1}>
                                    <TextFieldInput
                                        id='orderExecution'
                                        label='Order'
                                        value={attributeDTO?.orderExecution}
                                        setValue={setAttributeDTO}
                                        obj={attributeDTO}
                                        type={'number'}
                                    />
                                </Grid>
                                {
                                    ListId.ListValue.ActionsTest.click !== attributeDTO?.actionId && 
                                    ListId.ListValue.ActionsTest.ClickItemGridView !== attributeDTO?.actionId && 
                                    ListId.ListValue.ActionsTest.send !== attributeDTO?.actionId && 
                                    ListId.ListValue.ActionsTest.submit !== attributeDTO?.actionId ? (
                                            <Grid item xs={12} md={9}>
                                                <TextFieldInput
                                                    id={!isAttributeValue ? 'defaultValue' : 'value'}
                                                    label={!isAttributeValue ? 'Default Value' : 'Value'}
                                                    value={!isAttributeValue ? attributeDTO?.defaultValue : testAttributeValueDTO?.value}
                                                    setValue={!isAttributeValue ? setAttributeDTO : setTestAttributeValueDTO}
                                                    obj={!isAttributeValue ? attributeDTO : testAttributeValueDTO}
                                                    multiline={true}
                                                    rows={1}
                                                />
                                            </Grid>
                                        ) : (
                                            <Grid item xs={12} md={9}>
                                                <SelectField
                                                    id={!isAttributeValue ? 'defaultValue' : 'value'}
                                                    label={!isAttributeValue ? 'Default Value' : 'Value'}
                                                    value={!isAttributeValue ? attributeDTO?.defaultValue : testAttributeValueDTO?.value}
                                                    setValue={!isAttributeValue ? setAttributeDTO : setTestAttributeValueDTO}
                                                    obj={!isAttributeValue ? attributeDTO : testAttributeValueDTO}
                                                    valueList={booleanSelectList}
                                                />
                                            </Grid>
                                        )}
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <Grid item xs={12} md={6}>
                                    <TextFieldInput
                                        id={!isAttributeValue ? 'defaultValue' : 'value'}
                                        label={!isAttributeValue ? 'Default URL' : 'URL'}
                                        value={!isAttributeValue ? attributeDTO?.defaultValue : testAttributeValueDTO?.value}
                                        setValue={!isAttributeValue ? setAttributeDTO : setTestAttributeValueDTO}
                                        obj={!isAttributeValue ? attributeDTO : testAttributeValueDTO}
                                        multiline={true}
                                        rows={1}
                                    />
                                </Grid>
                                <Grid item xs={12} md={1}>
                                    <TextFieldInput
                                        id='orderExecution'
                                        label='Order'
                                        value={attributeDTO.orderExecution}
                                        setValue={setAttributeDTO}
                                        obj={attributeDTO}
                                        type={'number'}
                                    />
                                </Grid>
                            </React.Fragment>
                        )}
                        <Grid item xs={12} md={2}>
                            <FormGroup>
                                <FormControlLabel control={
                                    <Checkbox
                                        defaultChecked={attributeDTO?.updateAllTests}
                                        onChange={(event) => setAttributeDTO({ ...attributeDTO, updateAllTests: Boolean(event.target.value) } as AttributeDTO)}
                                        disabled={attributeDTO?.id ? false : true}
                                    />
                                } label="Update All Tests" />
                            </FormGroup>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='description'
                                label='Description'
                                value={attributeDTO?.description ?? ''}
                                setValue={setAttributeDTO}
                                obj={attributeDTO}
                                required={true}
                                multiline={true}
                                rows={2}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id={!isAttributeValue ? 'domainValues' : 'domainValues'}
                                label={!isAttributeValue ? 'Default Value' : 'Value'}
                                value={!isAttributeValue ? attributeDTO?.domainValues : testAttributeValueDTO?.domainValues}
                                setValue={!isAttributeValue ? setAttributeDTO : setTestAttributeValueDTO}
                                obj={!isAttributeValue ? attributeDTO : testAttributeValueDTO}
                                multiline={true}
                                rows={4}
                            />
                        </Grid>
                        {attributeDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={isAttributeValue ? testAttributeValueDTO?.id : attributeDTO?.id}
                                        hasDivider={false}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label='Created By'
                                        value={userList.find(x => x.id === (isAttributeValue ? testAttributeValueDTO?.createdById : attributeDTO.createdById))?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label='Creation Date'
                                        value={formatDateTime(String(isAttributeValue ? testAttributeValueDTO?.creationDateTime : attributeDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label='Updated By'
                                        value={userList.find(x => x.id === (isAttributeValue ? testAttributeValueDTO?.updatedById : attributeDTO.updatedById))?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label='Update Date'
                                        value={formatDateTime(String(isAttributeValue ? testAttributeValueDTO?.updateDateTime : attributeDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};